import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Phone, Calendar, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Services = () => {
  const services = [
    {
      icon: Globe,
      title: "AI-Powered Automotive Websites",
      description: "Fast, modern websites built to convert visitors into calls and bookings",
      image: "https://images.pexels.com/photos/70912/pexels-photo-70912.jpeg",
      features: [
        "Fast loading and mobile-optimized",
        "Built to convert visitors into customers",
        "Professional design that builds trust",
        "SEO-ready to show up on Google",
        "Easy to update - no tech skills needed",
        "Integrated booking and contact forms"
      ],
      benefits: [
        "Look as professional as the big chains",
        "Show up when customers search for you",
        "Capture leads 24/7 even when you're closed"
      ]
    },
    {
      icon: Phone,
      title: "24/7 AI Call & Chat Assistants",
      description: "Never miss a call again - AI answers after hours, weekends, and holidays",
      image: "https://images.pexels.com/photos/7709255/pexels-photo-7709255.jpeg",
      features: [
        "Answers calls 24/7 - nights, weekends, holidays",
        "Handles common questions about pricing, hours, services",
        "Captures customer name, phone, and what they need",
        "Sends leads instantly to you via text or email",
        "Sounds natural and professional",
        "Never puts customers on hold"
      ],
      benefits: [
        "Never lose another after-hours lead",
        "Customers get instant answers anytime",
        "You get qualified leads without lifting a finger"
      ]
    },
    {
      icon: Calendar,
      title: "Booking & Follow-Up Automation",
      description: "Let customers book online and automate your follow-ups",
      image: "https://images.pexels.com/photos/845451/pexels-photo-845451.jpeg",
      features: [
        "Online booking that syncs with your calendar",
        "Automatic appointment reminders via SMS and email",
        "Follow-up messages after service is complete",
        "Automatic review requests to build your reputation",
        "Missed-call recovery system",
        "Customer database to track and nurture leads"
      ],
      benefits: [
        "Stop playing phone tag with customers",
        "Reduce no-shows with automatic reminders",
        "Get more 5-star reviews without asking"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our <span className="text-orange-500">Services</span>
            </h1>
            <p className="text-xl text-gray-400">
              Simple, powerful tools to help your auto shop capture more leads, save time, and grow revenue
            </p>
          </div>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {services.map((service, idx) => (
              <div key={idx} className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="bg-orange-500/10 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                    <service.icon className="h-8 w-8 text-orange-500" />
                  </div>
                  
                  <h2 className="text-4xl font-bold text-white mb-4">
                    {service.title}
                  </h2>
                  
                  <p className="text-xl text-gray-400 mb-8">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-white mb-4">What's Included:</h3>
                    <div className="space-y-3">
                      {service.features.map((feature, fIdx) => (
                        <div key={fIdx} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-1" />
                          <span className="text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-8">
                    <h3 className="text-lg font-bold text-orange-500 mb-4">What This Means For You:</h3>
                    <div className="space-y-2">
                      {service.benefits.map((benefit, bIdx) => (
                        <div key={bIdx} className="flex items-start gap-3">
                          <ArrowRight className="h-5 w-5 text-orange-500 flex-shrink-0 mt-1" />
                          <span className="text-white font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link to="/contact">
                    <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 hover:scale-105">
                      Request a Demo
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>

                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="rounded-2xl shadow-2xl shadow-orange-500/20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Philosophy Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-400">
              No surprises. No hidden fees. No long-term contracts.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: CheckCircle, title: "One-Time Setup", desc: "Get everything configured and customized for your shop" },
              { icon: Clock, title: "Simple Monthly Fee", desc: "Predictable pricing. Cancel anytime." },
              { icon: Phone, title: "Ongoing Support", desc: "We maintain, update, and support everything" }
            ].map((item, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 text-center">
                <CardContent className="p-8">
                  <div className="bg-orange-500/10 w-16 h-16 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <item.icon className="h-8 w-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-6">
              Every shop is different. We'll create a custom package based on your needs and budget.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold transition-all duration-300">
                Get Custom Pricing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to See It In Action?
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            Book a free demo and we'll show you exactly how these systems work for shops like yours
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-10 py-6 text-xl font-bold shadow-2xl transition-all duration-300 hover:scale-105">
              Book Your Free Demo
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
